import { createRoot } from "react-dom/client";
import "./index.css";
import { initializeNativeRuntime } from './lib/nativeRuntime';
import { initializeNativeData } from './lib/nativeDataRuntime';

void initializeNativeRuntime().catch(() => console.warn('Native initialization unavailable.'));

// Auto-recover from stale dynamic-import chunks after a new deploy.
// When index.html is cached but references hashed chunks that no longer
// exist, dynamic imports throw. Reload once to fetch the fresh manifest.
const isChunkLoadError = (msg: string) =>
  /error loading dynamically imported module|Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(msg);

const tryReloadOnce = () => {
  try {
    const key = "__chunk_reload_at";
    const last = Number(sessionStorage.getItem(key) || 0);
    if (Date.now() - last > 10_000) {
      sessionStorage.setItem(key, String(Date.now()));
      window.location.reload();
    }
  } catch {
    window.location.reload();
  }
};

window.addEventListener("vite:preloadError", (e) => {
  e.preventDefault();
  tryReloadOnce();
});
window.addEventListener("error", (e) => {
  if (e?.message && isChunkLoadError(e.message)) tryReloadOnce();
});
window.addEventListener("unhandledrejection", (e) => {
  const msg = (e?.reason && (e.reason.message || String(e.reason))) || "";
  if (isChunkLoadError(msg)) tryReloadOnce();
});

const root = document.getElementById('root');
async function startApp() {
  await initializeNativeData();
  const { default: App } = await import('./App.tsx');
  if (root) createRoot(root).render(<App />);
}
void startApp();

// Web Vitals tracking: sampled for real visitors so the admin dashboard has
// real-user data. Loaded after idle so it never blocks first paint.
if (typeof window !== 'undefined') {
  const isAdminVitalsPage = window.location.pathname.startsWith('/admin/web-vitals');
  // 100% on the dashboard page (for verification), 25% sample elsewhere
  const shouldTrack = isAdminVitalsPage || Math.random() < 0.25;
  if (shouldTrack) {
    const start = () => import("./lib/webVitals").then(({ initWebVitals }) => initWebVitals());
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(start, { timeout: 3000 });
    } else {
      setTimeout(start, 3000);
    }
  }
}

